/**
 * CopilotEarningsCard — "Copilot earned you $X this month".
 *
 * The monetization justification for the entire subscription. Shows
 * the landlord the CAD Copilot generated / saved them in the last
 * 30 days, broken down by category. Rule-based (no LLM cost).
 *
 * Silent when there's nothing to show (< CA$50 total) — no false
 * flexing.
 */
import { useEffect, useState } from "react";
import { Sparkles, TrendingUp, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import "@/components/smart/SmartAccent.css";

function fmt(cad) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency", currency: "CAD", maximumFractionDigits: 0,
  }).format(cad || 0);
}

export default function CopilotEarningsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/copilot/earnings")
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;
  if (data.total_cad < 50) return null; // don't show a nothing-burger

  return (
    <section
      data-testid="copilot-earnings-card"
      className="mb-6 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden smart-accent-ring"
    >
      <div className="p-4 sm:p-5 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-500 via-sky-500 to-violet-500 text-white flex-none shadow-md">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700 inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Copilot earned you (last 30 days)
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="copilot-earnings-total">
              {fmt(data.total_cad)}
            </span>
            <span className="text-sm font-bold text-emerald-700">
              {data.roi_multiple}× your CA${data.plan_cost_cad}/mo plan
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 grid gap-2 p-4 sm:p-5 sm:grid-cols-2">
        {data.line_items.map((it) => (
          <div
            key={it.id}
            data-testid={`copilot-earnings-item-${it.id}`}
            className="p-3 rounded-xl bg-slate-50 border border-slate-100"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{it.label}</div>
              <div className="font-display text-lg font-extrabold text-emerald-700">{fmt(it.amount_cad)}</div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed mt-1">{it.sub}</p>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-2.5 flex items-center justify-between gap-3">
        <span className="text-[11px] text-slate-500">A landlord who paid a person the same result would owe ~CA${data.hourly_equivalent}/hr.</span>
        <a
          href="/billing"
          data-testid="copilot-earnings-upgrade"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-800"
        >
          Manage plan <ArrowRight className="w-3 h-3" />
        </a>
      </div>
    </section>
  );
}
